import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Alert, TabBar } from '@thegpvc/ui';
import { RefreshCw } from 'lucide-react';
import { api } from '../api/client';
import { PageHeader } from '../components/PageHeader';
import { PageContainer } from '../components/PageContainer';
import { SectionCard } from '../components/SectionCard';
import { TaskQueuePanel } from '../components/TaskQueuePanel';
import { ModelPricingEditor } from '../components/ModelPricingEditor';
import { text, bg, border, colWidth, cx } from '../styles/theme';
import { errorMessage } from '../utils/errorMessage';
import { installCommand } from '../utils/installCommand';
import type { UserSettings } from '../types/api';

const settingsTabs = [
  { id: 'general', label: 'General' },
  { id: 'admin', label: 'Admin' },
];

export function Settings() {
  const queryClient = useQueryClient();
  const [summaryLimit, setSummaryLimit] = useState(25);

  // Billing settings — mode inverts how usage economics render (spend vs plan
  // value). Local edits overlay the saved settings (no state-syncing effect);
  // the merged view is what renders and what Save submits.
  const [billingEdits, setBillingEdits] = useState<Partial<UserSettings>>({});
  const { data: savedSettings } = useQuery({
    queryKey: ['userSettings'],
    queryFn: () => api.getUserSettings(),
  });
  const billing: UserSettings = { billingMode: 'api', ...savedSettings, ...billingEdits };
  const setBilling = (next: UserSettings) => setBillingEdits((prev) => ({ ...prev, ...next }));
  // The project list edits as raw text (normalizing per keystroke would eat
  // commas mid-typing); parsed into a list only on save.
  const [apiProjectsText, setApiProjectsText] = useState<string | null>(null);
  const apiProjectsDisplay = apiProjectsText ?? (billing.apiProjects ?? []).join(', ');
  const saveBilling = () => {
    const apiProjects =
      apiProjectsText === null
        ? billing.apiProjects ?? []
        : apiProjectsText.split(',').map((p) => p.trim()).filter(Boolean);
    saveBillingMutation.mutate({ ...billing, apiProjects });
  };
  const saveBillingMutation = useMutation({
    mutationFn: (s: UserSettings) => api.updateUserSettings(s),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userSettings'] });
      queryClient.invalidateQueries({ queryKey: ['spendSummary'] });
    },
  });
  // Tab is URL-driven (?tab=admin) so /tasks can redirect straight to the
  // embedded task-queue admin surface.
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') === 'admin' ? 'admin' : 'general';

  // Fetch reprocessing status. A fetch failure must surface as an error, not
  // fall through to the zeroed counts below — "Total sessions: 0" reads as a
  // healthy empty state and hides the outage.
  const { data: status, isLoading: statusLoading, error: statusError } = useQuery({
    queryKey: ['reprocessing-status'],
    queryFn: () => api.getReprocessingStatus(),
    refetchInterval: 10000, // Refresh every 10 seconds
  });

  // Mutation for triggering reprocessing. Success/error are surfaced via the
  // mutation's own state (see the <Alert> blocks below) rather than a local
  // message string, so failures are never invisible.
  const reprocessMutation = useMutation({
    mutationFn: (scope: 'missing' | 'all') =>
      api.reprocessSessions(scope === 'all'),
    onSuccess: () => {
      // Refresh status after reprocessing
      queryClient.invalidateQueries({ queryKey: ['reprocessing-status'] });
    },
  });

  // Backfill per-sitting summaries for a subset of unsummarized sessions —
  // used to validate the Session Story chapter prose on real data before
  // running the full resummarize.
  const backfillSummariesMutation = useMutation({
    mutationFn: (limit: number) => api.backfillSummaries(limit),
  });

  const missingAutonomy = status ? parseInt(status.missingAutonomyScore, 10) : 0;
  const missingStats = status ? parseInt(status.missingStats, 10) : 0;
  const totalSessions = status ? parseInt(status.totalSessions, 10) : 0;
  const needsReprocessing = missingAutonomy > 0 || missingStats > 0;

  return (
    <PageContainer>
      <title>Dixie :: Settings</title>
      <PageHeader title="Settings">
        <TabBar
          variant="pill"
          items={settingsTabs}
          activeId={tab}
          onChange={(id: string) =>
            setSearchParams(id === 'general' ? {} : { tab: id }, { replace: true })
          }
        />
      </PageHeader>

      {tab === 'admin' ? (
        <TaskQueuePanel />
      ) : (
        <>
      <SectionCard title="Account">
        <p className={text.secondary}>
          Account settings will be available here.
        </p>
      </SectionCard>

      <SectionCard title="Billing" bodyClassName="space-y-4">
        <p className={text.secondary}>
          How you pay for Claude Code determines how usage economics render:
          API-key usage shows real spend; a subscription shows the
          API-equivalent value of your usage against the flat plan fee.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1">
            <span className={cx('text-xs', text.secondary)}>Billing mode</span>
            <select
              value={billing.billingMode ?? 'api'}
              onChange={(e) => setBilling({ ...billing, billingMode: e.target.value })}
              className={cx('rounded-lg px-3 py-1.5 text-sm border', bg.surface, border.default, text.primary)}
            >
              <option value="api">API key (pay per token)</option>
              <option value="subscription">Subscription (flat fee)</option>
            </select>
          </label>
          {billing.billingMode === 'subscription' && (
            <>
              <label className="flex flex-col gap-1">
                <span className={cx('text-xs', text.secondary)}>Plan name</span>
                <input
                  type="text"
                  placeholder="Max 20x"
                  value={billing.planName ?? ''}
                  onChange={(e) => setBilling({ ...billing, planName: e.target.value })}
                  className={cx(colWidth.inputMd, 'rounded-lg px-3 py-1.5 text-sm border', bg.surface, border.default, text.primary)}
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className={cx('text-xs', text.secondary)}>API-billed projects</span>
                <input
                  type="text"
                  placeholder="phi-project, other-project"
                  title="Comma-separated project names billed via API key regardless of the subscription (e.g. PHI work on a dedicated key)"
                  value={apiProjectsDisplay}
                  onChange={(e) => setApiProjectsText(e.target.value)}
                  className={cx(colWidth.inputLg, 'rounded-lg px-3 py-1.5 text-sm border', bg.surface, border.default, text.primary)}
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className={cx('text-xs', text.secondary)}>Monthly fee (USD)</span>
                <input
                  type="number"
                  min={0}
                  value={billing.planMonthlyFeeUsd ?? 0}
                  onChange={(e) => setBilling({ ...billing, planMonthlyFeeUsd: Math.max(0, Number(e.target.value) || 0) })}
                  className={cx(colWidth.inputSm, 'rounded-lg px-3 py-1.5 text-sm border', bg.surface, border.default, text.primary)}
                />
              </label>
            </>
          )}
          <Button
            variant="primary"
            size="sm"
            onClick={saveBilling}
            disabled={saveBillingMutation.isPending}
          >
            Save Billing
          </Button>
        </div>
        {saveBillingMutation.isError && (
          <Alert variant="error">Error: {errorMessage(saveBillingMutation.error)}</Alert>
        )}
        {saveBillingMutation.isSuccess && (
          <Alert variant="success">Billing settings saved.</Alert>
        )}
      </SectionCard>

      <ModelPricingEditor />

      <SectionCard title="CLI Configuration" bodyClassName="space-y-4">
        <p className={text.secondary}>
          To sync your Claude Code sessions, install and authenticate the Dixie
          daemon. The{' '}
          <Link to="/setup" className={text.link}>
            setup guide
          </Link>{' '}
          walks you through it from a fresh machine.
        </p>
        <p className={text.secondary}>Already installed? Upgrade to the latest daemon:</p>
        <pre className={cx('rounded-lg p-4 text-sm font-mono overflow-x-auto', bg.surface, border.default, 'border', text.primary)}>
{installCommand()}
        </pre>
      </SectionCard>

      <SectionCard title="Session Stats Reprocessing" bodyClassName="space-y-4">
        <p className={text.secondary}>
          Reprocess session logs to compute missing statistics like autonomy scores.
          Both actions operate on the most recent upload of each session, so{' '}
          <span className={text.primary}>Reprocess All</span> recomputes every
          session's stats (e.g. after adding a new field) without churning
          superseded re-sync uploads.
        </p>

        {/* Status display */}
        <div className={cx('rounded-lg p-4', bg.surface, border.default, 'border')}>
          {statusLoading ? (
            <p className={text.secondary}>Loading status…</p>
          ) : statusError ? (
            <Alert variant="error">
              Failed to load reprocessing status: {errorMessage(statusError)}
            </Alert>
          ) : (
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className={text.secondary}>Total sessions:</span>
                <span className={text.primary}>{totalSessions}</span>
              </div>
              <div className="flex justify-between">
                <span className={text.secondary}>Missing autonomy score:</span>
                <span className={needsReprocessing ? 'text-amber-600 dark:text-amber-400' : text.primary}>
                  {missingAutonomy}
                </span>
              </div>
              <div className="flex justify-between">
                <span className={text.secondary}>Missing all stats:</span>
                <span className={missingStats > 0 ? 'text-amber-600 dark:text-amber-400' : text.primary}>
                  {missingStats}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex gap-3">
          <Button
            variant="primary"
            size="sm"
            icon={<RefreshCw />}
            spin={reprocessMutation.isPending}
            onClick={() => reprocessMutation.mutate('missing')}
            disabled={reprocessMutation.isPending || !needsReprocessing}
          >
            Reprocess Missing
          </Button>
          <Button
            variant="secondary"
            size="sm"
            icon={<RefreshCw />}
            spin={reprocessMutation.isPending}
            onClick={() => reprocessMutation.mutate('all')}
            disabled={reprocessMutation.isPending}
          >
            Reprocess All
          </Button>
        </div>

        {/* Result — success and error both surfaced from mutation state. */}
        {reprocessMutation.isError && (
          <Alert variant="error">Error: {errorMessage(reprocessMutation.error)}</Alert>
        )}
        {reprocessMutation.isSuccess && (
          <Alert variant="success">{reprocessMutation.data.message}</Alert>
        )}
      </SectionCard>

      <SectionCard title="Session Summaries" bodyClassName="space-y-4">
        <p className={text.secondary}>
          Generate per-sitting summaries for a batch of sessions that don't
          have one yet. Each summarized session's chapters gain their own
          prose in the Session Story view. Start with a small batch to validate
          the results before running the full backfill.
        </p>

        <div className="flex items-end gap-3 flex-wrap">
          <label className="flex flex-col gap-1">
            <span className={cx('text-xs', text.secondary)}>Batch size</span>
            <input
              type="number"
              min={1}
              max={500}
              value={summaryLimit}
              onChange={(e) => setSummaryLimit(Math.max(1, Math.min(500, Number(e.target.value) || 1)))}
              className={cx(
                colWidth.inputSm,
                'rounded-lg px-3 py-1.5 text-sm border',
                bg.surface, border.default, text.primary,
              )}
            />
          </label>
          <Button
            variant="primary"
            size="sm"
            icon={<RefreshCw />}
            spin={backfillSummariesMutation.isPending}
            onClick={() => backfillSummariesMutation.mutate(summaryLimit)}
            disabled={backfillSummariesMutation.isPending}
          >
            Summarize Batch
          </Button>
        </div>

        {backfillSummariesMutation.isError && (
          <Alert variant="error">Error: {errorMessage(backfillSummariesMutation.error)}</Alert>
        )}
        {backfillSummariesMutation.isSuccess && (
          <Alert variant="success">{backfillSummariesMutation.data.message}</Alert>
        )}
      </SectionCard>

      <SectionCard title="Data Management">
        <p className={text.secondary}>
          Data export and deletion options will be available here.
        </p>
      </SectionCard>
        </>
      )}
    </PageContainer>
  );
}
